import React, { useContext, useEffect, useId, useState, useCallback, useMemo } from 'react';
import InterfaceContext, { PublicConfigurationContext } from './Context';
import { ThreeColumnPage } from './Components/ThreeColumnPage';
import { NavigationNode } from '@aidkitorg/types/lib/translation/permissions';
import { usePost, handleDataError } from './API';
import { useCookies } from 'react-cookie';
import { saveKeyToCookieAndRelocateToUrl, SpacedSpinner } from './Util';
import { getPortalToken, portalLogout } from './Hooks/PortalSession';
import { LoginApplyLink, NewApplicationLink, PortalCard, PortalExplanation, PortalExplanationSection, PortalPublicLink, ServedPortalPayments, ServedPortalSupportTicketForm, SubsurveyLink, TemplatedApplicationBlock } from '@aidkitorg/types/lib/survey';
import { REFRESH_SESSION_EVENT } from './Components/ApplicantSessionKeeper';
import buildConditionalContent from '@aidkitorg/types/lib/utils/conditionalContent';
import { GetApplicantCandidatesOutput, PotentialCandidate } from '@aidkitorg/aidkit/lib/application/multiplex';
import { evalDistroConditional } from '@aidkitorg/types/lib/eval';
import { extractTargetFieldsFromComponents } from '@aidkitorg/types/lib/traverse';
import { useLocalizedStrings } from './Localization';
import { useModularMarkdown } from './Hooks/ModularMarkdown';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Badge, Button, Card, Icon, Input, useIsMobile } from '@aidkitorg/component-library';
import { useHistory, useParams } from 'react-router-dom';
import { PortalSupportTicketFormComponent } from './Components/PortalSupportTicketForm';
import { PortalPaymentsComponent } from './Components/PortalPayments';
import { ApplicantPortalShell } from './Components/ApplicantPortalShell';
import { PortalTimelineComponent } from './Components/PortalTimeline';

export const MARKDOWN_NO_MARGIN_CLASSES = '[&_p]:mb-0 [&_h4]:mb-0 [&_h3]:mb-0 [&_h2]:mb-0 [&_h1]:mb-0';
const MARKDOWN_REPLACER = (str: string) => str.replace(' **\n','**\n').replace('^** ','**');

export function PortalExplanationComponent(props: { explanation: PortalExplanation, info: Record<string, string | undefined > | undefined }) {
  const { content, alignment, accordion, hideMissingFields } = props.explanation;
  const L = useLocalizedStrings();

  const marked = useModularMarkdown({
    content,
    info: { ...props.info },
    replacer: MARKDOWN_REPLACER,
    hideMissingFields
  });

  const header = useModularMarkdown({
    content: accordion?.header || '',
    info: { ...props.info },
    replacer: MARKDOWN_REPLACER,
    hideMissingFields
  });

  if (accordion) {
    return (
      <Accordion type={"single"} className="w-full text-sm py-2 mb-4">
        <AccordionItem value={'top item'} >
          <AccordionTrigger>
            <div className={`text-sm w-full ${MARKDOWN_NO_MARGIN_CLASSES}`} style={{textAlign: accordion.headerAlignment || 'left'}}>
              {header}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            {marked}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    )
  }
  return (
    <div style={{ textAlign: alignment || 'left' } as React.CSSProperties} >
      {marked}
    </div>
  );
}

function PortalExplanationSectionComponent(props: { section: PortalExplanationSection }) {
  const context = useContext(InterfaceContext);
  const [query, setQuery] = useState('');
  const searchInputId = useId();

  const children = props.section.components || [];
  const search = props.section.search;

  const searchableText = useMemo(() => (
    search
      ? children.map(child => {
        const parts = [child.content, ...(child.accordion?.header ? [child.accordion.header] : [])];
        return parts
          .flatMap(part => buildConditionalContent({ content: part, info: {}, lang: context.lang }))
          .join(' ')
          .toLowerCase();
      })
      : []
  ), [children, context.lang, search]);

  const keywords = query.trim().toLowerCase().split(/\s+/).filter(Boolean);

  const visibleChildren = search
    ? children.filter((_child, index) =>
      keywords.every(keyword => searchableText[index].includes(keyword)))
    : children;

  const label = search?.label?.[context.lang]
    || search?.label?.en
    || 'Search questions...';

  const noResultsText = search?.noResultsText?.[context.lang]
    || search?.noResultsText?.en
    || 'No results found.';

  const headerMarked = useModularMarkdown({
    content: props.section.header || '',
    info: {},
    replacer: MARKDOWN_REPLACER,
    hideMissingFields: props.section.hideMissingFields
  });

  const body = (
    <div className={`flex flex-col gap-3 px-1 ${props.section.accordion ? '[&>*:last-child>*]:border-b-0' : ''}`}>
      {search && (
        <div className="flex flex-col gap-1.5">
          <label htmlFor={searchInputId} className="flex items-center gap-1 text-sm font-medium text-[var(--ak-color-typography-text)]">
            <Icon name="MagnifyingGlassIcon" size="sm" className="shrink-0" />
            {label}
          </label>
          <Input
            id={searchInputId}
            label={label}
            showLabel={false}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      )}
      {visibleChildren.length > 0
        ? visibleChildren.map((child, index) => (
          <PortalExplanationComponent key={index} explanation={child} info={{}} />
        ))
        : search && <p className="text-sm text-slate-500 text-center py-2">{noResultsText}</p>}
    </div>
  );

  const accordion = props.section.accordion;
  if (accordion) {
    return (
      <Accordion type={"single"} className="w-full text-sm py-2 mb-4">
        <AccordionItem value={'top item'}>
          <AccordionTrigger>
            <div className={`text-sm w-full ${MARKDOWN_NO_MARGIN_CLASSES}`} style={{ textAlign: props.section.headerAlignment || 'left' }}>
              {headerMarked}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            {body}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    );
  }

  return (
    <div className="mb-4 flex flex-col gap-3">
      <div style={{ textAlign: props.section.headerAlignment || 'left' }}>
        {headerMarked}
      </div>
      {body}
    </div>
  );
}

export function PortalSubsurveyLink(props: {subsurveyLink: SubsurveyLink, candidate: PotentialCandidate, Viewer: 'screener' | 'applicant', displayMode?: 'button' | 'row' }) {
  const resume = usePost("/subsurvey/resume", { token: () => props.candidate.resumeToken });
  const history = useHistory();

  const doResume = async (params: {
    resumptionToken: string,
  }) => {
    if (props.candidate.applicant.kind === 'ingested') {
      const newURL = `${window.location.protocol}//${window.location.host}/p/apply/?key=${params.resumptionToken}`;
      saveKeyToCookieAndRelocateToUrl(newURL);
      return;
    }

    let resumeToken = props.candidate.resumeToken;
    const previous = await resume({
      form_name: 'apply'
    });

    if (previous?.newToken) {
      resumeToken = previous?.newToken;
    }

    // create a local id, save the token and info to the session, and redirect to the apply page
    // with the local id so it knows where the data is.
    const id = Math.random().toString(36).replace(/[^a-z]+/g, '').substr(0, 5);
    window.sessionStorage['auth:' + id] = resumeToken;
    window.sessionStorage['cache:' + id] = JSON.stringify(previous?.info);
    history.push(`/apply/${id}`);
  }

  const link = props.candidate.subsurveys?.find(subsurvey => {
    // get the path to verify applicant can access the link
    const path = new URL(subsurvey.link).pathname;
    const result = path.split('/').pop();
    return result === props.subsurveyLink.subsurvey;
  })

  const marked = useModularMarkdown({
    content: props.subsurveyLink.label,
    info: { ...props.candidate.info },
    replacer: MARKDOWN_REPLACER,
  });

  const description = useModularMarkdown({
    content: props.subsurveyLink.description || '',
    info: { ...props.candidate.info },
    replacer: MARKDOWN_REPLACER,
  });

  // Don't show these if the applicant doesn't have access to the link
  if (!(link || (props.subsurveyLink.subsurvey === 'apply' && props.candidate.resumeToken))) {
    return null;
  }

  const handleClick = props.subsurveyLink.subsurvey === 'apply'
    ? () => doResume({ resumptionToken: props.candidate.resumeToken })
    : () => { window.location.href = link?.link || window.location.href; };

  if (props.displayMode === 'row') {
    const disabled = props.Viewer === 'screener';
    const rowVariantClass = {
      primary: 'bg-blue-50 border-blue-200 hover:bg-blue-100',
      danger: 'bg-red-50 border-red-200 hover:bg-red-100',
      secondary: 'bg-white border-slate-200 hover:bg-slate-50',
      link: 'bg-white border-slate-200 hover:bg-slate-50',
    }[props.subsurveyLink.variant || 'secondary'];
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={handleClick}
        className={`w-full flex items-center gap-4 py-3 px-4 text-left rounded-lg border ${rowVariantClass} disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1`}
      >
        <div className="min-w-0 flex-1">
          <div className={`text-sm font-semibold text-slate-900 ${MARKDOWN_NO_MARGIN_CLASSES}`}>
            {marked}
          </div>
          {props.subsurveyLink.description ? (
            <div className={`text-sm mt-1 ${MARKDOWN_NO_MARGIN_CLASSES}`}>
              {description}
            </div>
          ) : null}
        </div>
        <Icon name="ChevronRightIcon" className="flex-shrink-0 text-slate-400" />
      </button>
    );
  }

  return <div className="w-full" style={{textAlign: props.subsurveyLink.alignment || 'left'}}>
    {props.subsurveyLink.description ? (
      <div className={`text-sm mb-2 ${MARKDOWN_NO_MARGIN_CLASSES}`}>
        {description}
      </div>
    ) : null}
    <Button
      disabled={props.Viewer === 'screener'}
      className={`mb-4 pr-5 pl-5 ${MARKDOWN_NO_MARGIN_CLASSES}`}
      onClick={handleClick}
      variant={props.subsurveyLink.variant || 'secondary'}
    >
      {marked}
    </Button>
  </div>;
}

export function PortalPublicLinkComponent(props: { link: PortalPublicLink, info: Record<string, string | undefined> | undefined, displayMode?: 'button' | 'row', isInGroup?: boolean }) {
  const history = useHistory();
  const { url, openInNewTab, alignment, variant, description } = props.link;

  const marked = useModularMarkdown({
    content: description || '',
    info: { ...props.info },
    replacer: MARKDOWN_REPLACER,
  });
  const label = useModularMarkdown({
    content: props.link.label,
    info: { ...props.info },
    replacer: MARKDOWN_REPLACER,
    noParagraphs: true,
  });

  // Only same-domain paths (/…) and absolute http(s) URLs are allowed — guards
  // against javascript:/data: and other script-y schemes. A same-domain path
  // must be a single leading slash: reject protocol-relative URLs (//host, and
  // the /\ backslash variant browsers normalize to //) which resolve off-site.
  const isSafe = /^https?:\/\//i.test(url)
    || (url.startsWith('/') && url[1] !== '/' && url[1] !== '\\');

  const handleClick = () => {
    if (!isSafe) {
      console.error('PortalPublicLink: refusing to navigate to unsafe url', url);
      return;
    }
    if (openInNewTab) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else if (url.startsWith('/')) {
      history.push(url);
    } else {
      window.location.href = url;
    }
  };

  // Inside a Portal Card, links render as full-width rows to match the
  // Subsurvey Link presentation rather than as a standalone button.
  if (props.displayMode === 'row') {
    const rowVariantClass = {
      primary: 'bg-blue-50 border-blue-200 hover:bg-blue-100',
      danger: 'bg-red-50 border-red-200 hover:bg-red-100',
      secondary: 'bg-white border-slate-200 hover:bg-slate-50',
      link: 'bg-white border-slate-200 hover:bg-slate-50',
    }[variant || 'primary'];
    return (
      <button
        type="button"
        onClick={handleClick}
        className={`w-full flex items-center gap-4 py-4 px-4 text-left rounded-lg border ${rowVariantClass} focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1`}
      >
        <div className="min-w-0 flex-1">
          <div className={`text-sm font-semibold text-slate-900 ${MARKDOWN_NO_MARGIN_CLASSES}`}>
            {label}
          </div>
          {description ? (
            <div className={`text-sm mt-1 ${MARKDOWN_NO_MARGIN_CLASSES}`}>
              {marked}
            </div>
          ) : null}
        </div>
        <Icon name="ChevronRightIcon" className="flex-shrink-0 text-slate-400" />
      </button>
    );
  }

  if (props.isInGroup) {
    return (
      <div>
        {description ? (
          <div className={`text-sm mb-2 ${MARKDOWN_NO_MARGIN_CLASSES}`}>
            {marked}
          </div>
        ) : null}
        <Button
          className={`pr-5 pl-5 ${MARKDOWN_NO_MARGIN_CLASSES}`}
          onClick={handleClick}
          variant={variant || 'primary'}
        >
          {label}
        </Button>
      </div>
    );
  }

  return (
    <div style={{ textAlign: alignment || 'left' }} className="mb-4">
      {description ? (
        <div className={`text-sm mb-2 ${MARKDOWN_NO_MARGIN_CLASSES}`}>
          {marked}
        </div>
      ) : null}
      <Button
        className={`pr-5 pl-5 ${MARKDOWN_NO_MARGIN_CLASSES}`}
        onClick={handleClick}
        variant={variant || 'primary'}
      >
        {label}
      </Button>
    </div>
  );
}

export function PortalNewApplicationLink(props: { newApplicationLink: NewApplicationLink, canStartNewToken: string, Viewer: 'screener' | 'applicant' }) {
  const history = useHistory();

  const marked = useModularMarkdown({
    content: props.newApplicationLink.label,
    info: {},
    replacer: MARKDOWN_REPLACER,
  });

  const description = useModularMarkdown({
    content: props.newApplicationLink.description || '',
    info: {},
    replacer: MARKDOWN_REPLACER,
  });

  const resume = usePost("/subsurvey/resume", { token: () => props.canStartNewToken });
  const doResume = async (params: {
    resumptionToken: string,
  }) => {
    let confirmToken = props.canStartNewToken;
    const previous = await resume({
      form_name: 'apply'
    });
    if (previous?.newToken) {
      confirmToken = previous?.newToken;
    }
    // create a local id, save the token and info to the session, and redirect to the apply page
    // with the local id so it knows where the data is.
    // TODO: add ref = 'portal' to info so we know this app came from.
    // Double check that this info is actually saved to the session.
    const id = Math.random().toString(36).replace(/[^a-z]+/g, '').substr(0, 5);
    window.sessionStorage['auth:' + id] = confirmToken;
    window.sessionStorage['cache:' + id] = JSON.stringify(previous?.info || {});
    history.push('/apply/?ref=portal&localid=' + id);
  }

  return <div className="w-full" style={{textAlign: props.newApplicationLink.alignment || 'left'}}>
    {props.newApplicationLink.description ? (
      <div className={`text-sm mb-2 ${MARKDOWN_NO_MARGIN_CLASSES}`}>
        {description}
      </div>
    ) : null}
    <Button
      disabled={props.Viewer === 'screener'}
      className={`mb-4 pr-5 pl-5 ${MARKDOWN_NO_MARGIN_CLASSES}`}
      onClick={ (e) => doResume({ resumptionToken: props.canStartNewToken }) }
      variant={props.newApplicationLink.variant || 'secondary'}
    >
      {marked}
    </Button>
  </div>;
}

export function PortalLoginApplyLink(props: { newApplicationLink: LoginApplyLink, Viewer: 'screener' | 'applicant', portalRef?: string }) {
  const history = useHistory();

  const marked = useModularMarkdown({
    content: props.newApplicationLink.label,
    info: {},
    replacer: MARKDOWN_REPLACER,
  });

  const description = useModularMarkdown({
    content: props.newApplicationLink.description || '',
    info: {},
    replacer: MARKDOWN_REPLACER,
  });

  let link = props.newApplicationLink.subsurvey === 'apply' ? '/apply/' : `/p/${props.newApplicationLink.subsurvey}/`;
  if (props.portalRef) {
    link = `${link}?ref=${encodeURIComponent(props.portalRef)}`;
  }

  return <div className="w-full" style={{textAlign: props.newApplicationLink.alignment || 'left'}}>
    {props.newApplicationLink.description ? (
      <div className={`text-sm mb-2 ${MARKDOWN_NO_MARGIN_CLASSES}`}>
        {description}
      </div>
    ) : null}
    <Button
      disabled={props.Viewer === 'screener'}
      className={`mb-4 pr-5 pl-5 ${MARKDOWN_NO_MARGIN_CLASSES}`}
      onClick={ (e) => history.push(link)}
      variant={props.newApplicationLink.variant || 'secondary'}
    >
      {marked}
    </Button>
  </div>;
}

function PortalCardComponent(props: { card: PortalCard, candidate: PotentialCandidate, Viewer: 'screener' | 'applicant' }) {
  const cardHeader = useModularMarkdown({
    content: props.card.header,
    info: { ...props.candidate.info },
    replacer: MARKDOWN_REPLACER,
  });

  const statusBadge = useMemo(() => (
    props.card.statusBadges?.find(badge => evalDistroConditional(badge.conditionalOn, props.candidate.info || {}))
  ), [props.card.statusBadges, props.candidate.info]);

  const statusBadgeLabel = useModularMarkdown({
    content: statusBadge?.label || '',
    info: { ...props.candidate.info },
    replacer: MARKDOWN_REPLACER,
    noParagraphs: true,
  });

  const visibleComponents = useMemo(() => (
    props.card.components.filter(component => {
      if (component.conditionalOn) {
        return evalDistroConditional(component.conditionalOn, props.candidate.info || {});
      }
      return true;
    })
  ), [props.card.components, props.candidate.info]);

  return (
    <Card className="mb-4 overflow-hidden border border-slate-200" contentClassName="p-0">
      <div
        className={`bg-slate-50 px-5 py-4 border-b border-slate-200 flex flex-wrap items-start gap-3 ${MARKDOWN_NO_MARGIN_CLASSES}`}
      >
        <div className="min-w-0 flex-1" style={{ textAlign: props.card.headerAlignment || 'left' }}>
          {cardHeader}
        </div>
        {statusBadge && (
          <Badge variant={statusBadge.variant || 'neutral'} className="ml-auto max-w-full whitespace-normal text-left">
            {statusBadgeLabel}
          </Badge>
        )}
      </div>
      <div className="px-5 py-4 flex flex-col gap-3">
        {visibleComponents.map((component, index) => {
          switch (component.kind) {
            case 'Portal Explanation':
              return (
                <div key={index} className="text-sm">
                  <PortalExplanationComponent explanation={component} info={props.candidate.info} />
                </div>
              );
            case 'Subsurvey Link':
              return <PortalSubsurveyLink subsurveyLink={component} candidate={props.candidate} key={index} Viewer={props.Viewer} displayMode="row" />;
            case 'Portal Payments':
              // getPortal resolves .payments onto the component before serving.
              return <PortalPaymentsComponent component={component as unknown as ServedPortalPayments} candidate={props.candidate} key={index} Viewer={props.Viewer} />;
            case 'PortalTimeline':
              return <PortalTimelineComponent timeline={component} info={props.candidate.info} key={index} />;
            case 'Portal Public Link':
              return <PortalPublicLinkComponent key={index} link={component} info={props.candidate.info} displayMode="row" />;
            default:
              console.log('Unsupported component type:', component);
              return <div key={index}>Unsupported component type: {(component as any).kind} </div>;
          }
        })}
      </div>
    </Card>
  );
}

function ApplicantTemplateComponent(props: { candidates: GetApplicantCandidatesOutput | null, applicantTemplate: TemplatedApplicationBlock, Viewer: 'screener' | 'applicant' }) {
  const appsToInclude = props.candidates ? props.candidates.candidates.filter((candidate) => {
    if (candidate.info) {
      return evalDistroConditional(props.applicantTemplate.appsToInclude, candidate.info);
    } else {
      return false;
    }
  }) : [];

  const header = useModularMarkdown({
    content: props.applicantTemplate.header,
    info: {count: appsToInclude.length.toString()},
    replacer: MARKDOWN_REPLACER,
  });

  const renderComponent = useCallback((component: any, index: number, app: PotentialCandidate) => {
    if (component.conditionalOn) {
      if (!evalDistroConditional(component.conditionalOn, app.info || {})) return null;
    }

    switch (component.kind) {
      case 'Portal Explanation':
        return <PortalExplanationComponent explanation={component} info={app.info} key={index} />
      case 'Subsurvey Link':
        return <PortalSubsurveyLink subsurveyLink={component} candidate={app} key={index} Viewer={props.Viewer} />
      case 'Portal Card':
        return <PortalCardComponent card={component} candidate={app} key={index} Viewer={props.Viewer} />
      case 'Portal Payments':
        return <PortalPaymentsComponent component={component} candidate={app} key={index} Viewer={props.Viewer} />
      case 'PortalTimeline':
        return <PortalTimelineComponent timeline={component} info={app.info} key={index} />
      case 'Portal Public Link':
        return <PortalPublicLinkComponent key={index} link={component} info={app.info} />
      default:
        console.log('Unsupported component type:', component);
        return <div key={index}>Unsupported component type: {component.kind} </div>;
    }
  }, [props.candidates]);

  if (appsToInclude.length === 0) {
    if (props.applicantTemplate.hideIfEmpty || props.applicantTemplate.ifEmpty === 'hide') return null;
    if (props.applicantTemplate.ifEmpty?.kind === 'Templated Application Block') return <ApplicantTemplateComponent candidates={props.candidates} applicantTemplate={props.applicantTemplate.ifEmpty} Viewer={props.Viewer}/>;
  }

  const outerClass = props.applicantTemplate.background === 'transparent'
    ? "w-full text-sm mb-4"
    : "w-full text-sm bg-slate-50 py-2 px-4 mb-4";

  if (props.applicantTemplate.accordion) return (
    <Accordion type={"single"} className={outerClass}>
      <AccordionItem value={'top item'} className="border-none">
        <AccordionTrigger>
          <div className={`text-sm w-full ${MARKDOWN_NO_MARGIN_CLASSES}`} style={{textAlign: props.applicantTemplate.headerAlignment || 'left'}}>
            {header}
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <div className="divide-y divide-gray-200 b-none">
            {appsToInclude.map(app => {
              const appID = app.applicant.kind === 'ingested' ? app.applicant.uid : app.applicant.dynamo_uid
              return (
                <div key={appID} className="pb-3 pt-3">
                  {props.applicantTemplate.components.map((component, index) => renderComponent(component, index, app))}
                </div>
              );
            })}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );

  return (
    <div className={outerClass}>
      <div style={{textAlign: props.applicantTemplate.headerAlignment || 'left'}}>
        {header}
      </div>
      {appsToInclude.map(app => {
        const appID = app.applicant.kind === 'ingested' ? app.applicant.uid : app.applicant.dynamo_uid
        return (
          <div key={appID}>
            {props.applicantTemplate.components.map((component, index) => renderComponent(component, index, app))}
          </div>
        );
      })}
    </div>
  );
}

export function ApplicantPortalPage(props: {
  Viewer: 'screener' | 'applicant',
  /** Override the applicant uid (defaults to the :uid route param). */
  uid?: string,
  /** Override the portal page path (defaults to the last URL path segment). */
  portalPath?: string,
  /**
   * Render these already-resolved components instead of fetching them from
   * /applicant/get_portal. Used by the distro-editor preview to render
   * unpublished, locally-resolved config. When undefined, the page fetches
   * the published config as normal.
   */
  componentsOverride?: any[],
  /**
   * Marks this render as the distro-editor preview. Suppresses the published
   * nav fetch and derives logged-in state from whether a `uid` was passed
   * (uid = a specific applicant's logged-in view; no uid = the public,
   * logged-out experience with login/apply CTAs).
   *
   * It also makes the chrome render the applicant's new-portal experience
   * (header, account menu, drawer layout) regardless of `Viewer`. The logged-in
   * preview keeps Viewer='screener' so the staff token drives the candidate/
   * data fetches, but visually it should look like what the applicant sees —
   * so the applicant-vs-staff chrome gates treat the preview as the applicant.
   */
  isDistroPreview?: boolean,
  /**
   * Render bare page content only, without this page's own chrome (its
   * ThreeColumnPage/ApplicantPortalShell nav and header). Used when a host
   * page — e.g. AdminPanelApplicantPage — already provides that chrome
   * (sidebar, section dropdown, notes panel) and just wants the portal
   * page's components embedded into its own main content area.
   */
  embedded?: boolean,
}) {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [nav, setNav] = useState<NavigationNode[]>([]);
  const [components, setComponents] = useState<any[]>([]);
  const [isComponentsLoading, setIsComponentsLoading] = useState<boolean>(true);
  const [candidates, setCandidates] = useState<GetApplicantCandidatesOutput | null>(null);
  const [isCandidatesLoading, setIsCandidatesLoading] = useState<boolean>(true);
  const [applicantPortalPageNotEnabled, setApplicantPortalPageNotEnabled] = useState(false);
  const { uid: uidParam } = useParams() as Record<string, string>;
  const uid = props.uid ?? uidParam;


  const context = useContext(InterfaceContext);
  const publicConfig = useContext(PublicConfigurationContext);
  const [portalRef, setPortalRef] = useState<string | undefined>(() => {
    try {
      return context.sessionStorage?.getItem('portal_ref') || undefined;
    } catch {
      return undefined;
    }
  });
  const [cookies, setCookie] = useCookies(['portal', 'auth_token']);

  const getApplicantCandidates = usePost("/applicant/get_candidates", { token: () => props.Viewer === 'applicant' ? getPortalToken() : cookies['auth_token'] });
  const getScreenerApplicantCandidates = usePost("/applicant/get_candidates");
  const refreshTokens = usePost("/applicant/refresh_token", { token: () => props.Viewer === 'applicant' ? getPortalToken() : cookies['auth_token'], handleErrors: () => {} });

  // In the distro-editor preview we render the in-progress (unpublished) config,
  // whose fields aren't in the published survey the backend derives the portal
  // field list from. Send the fields this previewed page references so its
  // conditionals and interpolation resolve. Backend honors this for admins only.
  //
  // Keyed as a sorted CSV so the value only changes when the *set* of referenced
  // fields changes — the candidate fetch then re-triggers on field changes (a new
  // $interpolation or conditionalOn field) but not on every unrelated edit.
  const previewTargetFieldsKey = useMemo(
    () => props.isDistroPreview && props.componentsOverride
      ? Array.from(extractTargetFieldsFromComponents(props.componentsOverride)).sort().join(',')
      : '',
    [props.isDistroPreview, props.componentsOverride],
  );
  const previewTargetFields = useMemo(
    () => previewTargetFieldsKey ? previewTargetFieldsKey.split(',') : undefined,
    [previewTargetFieldsKey],
  );

  const getPortalComponents = usePost("/applicant/get_portal", {
    token: () => props.Viewer === 'applicant' ? getPortalToken() : cookies['auth_token'],
    handleErrors: (e, toastHandler) => {
      if ('error' in e && e.error === 'not_enabled') {
        setApplicantPortalPageNotEnabled(true);
      } else {
        handleDataError(e, toastHandler);
      }
    },
  });
  const history = useHistory();
  const L = useLocalizedStrings();

  const path = props.portalPath ?? window.location.pathname.split('/').filter(Boolean).pop();

  useEffect(() => {
    if (props.Viewer !== 'applicant') return;
    try {
      const params = new URLSearchParams(window.location.search);
      const incomingRef = params.get('ref');
      if (incomingRef) {
        context.sessionStorage?.setItem('portal_ref', incomingRef);
        setPortalRef(incomingRef);
      } else if (!portalRef) {
        const stored = context.sessionStorage?.getItem('portal_ref');
        if (stored) setPortalRef(stored);
      }
    } catch {
      // ignore storage failures
    }
  }, [props.Viewer, context.sessionStorage, portalRef]);

  // Have to pass document cookie directly to usePost because it doesn't update when the cookie changes
  // This is a hacky workaround and will only update on rerenders. But react doesn't track cookies.
  const getNav = usePost("/applicant/navigation", { token: () => getPortalToken() || '' });
  const getScreenerNav = usePost("/navigation");


  useEffect(() => {
    // In the distro-editor preview (componentsOverride set) there is no nav to
    // show: it would come from the published /navigation endpoint and so be
    // inconsistent with the unpublished config the page itself renders. An
    // empty nav also makes ThreeColumnPage suppress the sidebar column.
    // Embedded renders skip this fetch too — the host page owns nav/chrome.
    if (props.isDistroPreview || props.componentsOverride !== undefined || props.embedded) {
      setNav([]);
      return;
    }
    (async () => {
      const response = (
        props.Viewer === 'screener' ? await getScreenerNav({ lang: context.lang, applicant: uid })
          : await getNav({ lang: context.lang })
      )
      setNav(response.navigation);
    })();
  }, [document.cookie, getNav, context.lang, props.isDistroPreview, props.componentsOverride, props.embedded]);

  useEffect(() => {
    if (props.isDistroPreview) {
      // In preview, logged-in state is whichever view the editor selected: a
      // uid means preview that applicant's logged-in page; no uid means the
      // public, logged-out experience.
      setIsLoggedIn(!!props.uid);
      return;
    }
    if (props.Viewer === 'applicant') {
      setIsLoggedIn(!!getPortalToken());
      return;
    }
    setIsLoggedIn(true);
  }, [document.cookie, props.isDistroPreview, props.uid]);

  useEffect(() => {
    let cancelled = false;

    const doGetApplicantCandidates = async () => {
      if (!isLoggedIn) {
        setIsCandidatesLoading(false);
        return;
      }
      // Wait for public config so the expired-token branch can tell a new-portal
      // program (clean logout) from a legacy one (show notice), and so portalLogout
      // reads the right logoutRedirectPath. Keep the spinner up meanwhile (don't
      // render portal components against null candidates); publicConfig is in this
      // effect's deps, so it re-runs once config loads.
      if (!Object.keys(publicConfig || {}).length) {
        setIsCandidatesLoading(true);
        return;
      }
      setIsCandidatesLoading(true);

      if (props.Viewer === 'screener') {
        const candidatesResults = await getScreenerApplicantCandidates({ info: { uid }, mode: 'portal', additionalTargetFields: previewTargetFields })
        // A newer fetch (e.g. the preview's field set changed) superseded this
        // one; drop the stale response so it can't overwrite the latest data.
        if (cancelled) return;
        setCandidates(candidatesResults);
      } else {
        const candidatesResults = await getApplicantCandidates({ info: {}, mode: 'portal' })
        if (cancelled) return;
        if (candidatesResults.error && candidatesResults.error === 'expired_token') {
          // Session expired — clear it and redirect to the logged-out portal via
          // the same path the session keeper uses. New-portal programs were warned
          // by the keeper's inactivity prompt, so log out cleanly. Legacy programs
          // have no such prompt, so still surface a notice first rather than
          // silently teleporting them to the home page.
          if (!publicConfig?.applicantPortal) {
            alert(L.apply.youve_been_logged_out);
          }
          portalLogout(publicConfig);
          return;
        }
        setCandidates(candidatesResults);
      }
      setIsCandidatesLoading(false);
    };

    doGetApplicantCandidates()
    return () => { cancelled = true; };
  }, [isLoggedIn, getApplicantCandidates, getScreenerApplicantCandidates, uid, L, history, props.Viewer, previewTargetFields, publicConfig]);

  // Slide the subsurvey-link tokens rendered on the portal home along with the
  // session. The subsurvey `?key=` tokens are stamped at get_candidates time and
  // would otherwise expire out from under a long-open portal page; when
  // ApplicantSessionKeeper signals activity, re-mint them (cheaply, in one call)
  // and rewrite the links so they stay valid as long as the session does.
  useEffect(() => {
    if (props.Viewer !== 'applicant') return;
    const onRefresh = async () => {
      const tokens: string[] = [];
      (candidates?.candidates || []).forEach((c) =>
        (c.subsurveys || []).forEach((s) => {
          try {
            const k = new URL(s.link).searchParams.get('key');
            if (k && k !== 'no_token') tokens.push(k);
          } catch { /* non-URL link; skip */ }
        })
      );
      if (!tokens.length) return;
      try {
        const res = await refreshTokens({ additionalTokens: tokens });
        const map = (res && 'refreshed' in res && res.refreshed) || {};
        if (!Object.keys(map).length) return;
        setCandidates((prev) => prev && ({
          ...prev,
          candidates: prev.candidates.map((c) => ({
            ...c,
            subsurveys: (c.subsurveys || []).map((s) => {
              try {
                const u = new URL(s.link);
                const k = u.searchParams.get('key');
                const fresh = k ? map[k] : undefined;
                if (fresh) {
                  u.searchParams.set('key', fresh);
                  return { ...s, link: u.toString() };
                }
              } catch { /* skip */ }
              return s;
            }),
          })),
        }));
      } catch { /* keepalive failure is non-fatal */ }
    };
    window.addEventListener(REFRESH_SESSION_EVENT, onRefresh);
    return () => window.removeEventListener(REFRESH_SESSION_EVENT, onRefresh);
  }, [candidates, props.Viewer, refreshTokens]);

  useEffect(() => {
    // Preview mode: render the components resolved from the in-progress distro
    // config instead of fetching the published page.
    if (props.componentsOverride !== undefined) {
      setComponents(props.componentsOverride);
      setIsComponentsLoading(false);
      return;
    }
    // Load dynamic components for the portal
    const loadComponents = async () => {
      if (!path) return;
      setIsComponentsLoading(true);
      try {
        const response = await getPortalComponents({ portalPage:  path });
        if (response.error) {
          throw new Error(response.error);
        } else {
          setComponents(response.portalPage?.components || []);
        };
      } catch (error) {
        console.error('Error loading portal components:', error);
      } finally {
        setIsComponentsLoading(false);
      }
    };
    loadComponents();
  }, [isLoggedIn, getPortalComponents, context.lang, path, props.componentsOverride]);

  const renderComponent = useCallback((component: any, index: number) => {
    switch ( component.showWhen ) {
      case 'Always':
        break;
      case 'LoggedIn':
        if (!isLoggedIn) return null;
        break;
      case 'LoggedOut':
        if (isLoggedIn) return null;
        break;
      default:
        break;
    };

    switch (component.kind) {
      case 'Templated Application Block':
        return <ApplicantTemplateComponent key={index} candidates={candidates} applicantTemplate={component} Viewer={props.Viewer} />;
      case 'Portal Explanation':
        return <PortalExplanationComponent key={index} explanation={component} info={{}} />
      case 'Portal Explanation Section':
        return <PortalExplanationSectionComponent key={index} section={component} />
      case 'New Application Link':
        return candidates?.canStartNewToken && isLoggedIn ? 
          <PortalNewApplicationLink key={index} newApplicationLink={component} canStartNewToken={candidates.canStartNewToken} Viewer={props.Viewer} />
          : null;
      case 'Login Apply Link':
        return <PortalLoginApplyLink key={index} newApplicationLink={component} Viewer={props.Viewer} portalRef={portalRef} />
      case 'Portal Support Ticket Form':
        // getPortal serves this component with the channel's issue-type options
        // resolved in (the authored type only carries `required`).
        return <PortalSupportTicketFormComponent key={index} component={component as ServedPortalSupportTicketForm} portalPage={path || ''} formIndex={index} Viewer={props.Viewer} />
      case 'Portal Public Link':
        // Page level has no single candidate, so no info to interpolate.
        return <PortalPublicLinkComponent key={index} link={component} info={{}} />
      default:
        console.log('Unsupported component type:', component);
        return <div key={index}>Unsupported component type: {component.kind} </div>;
    }
  }, [isLoggedIn, candidates, portalRef, props.Viewer, path]);

  const renderComponentList = useCallback((comps: any[]) => {
    const isVisible = (comp: any) => {
      if (comp.showWhen === 'LoggedIn') return isLoggedIn;
      if (comp.showWhen === 'LoggedOut') return !isLoggedIn;
      return true;
    };
    const result: React.ReactNode[] = [];
    let i = 0;
    while (i < comps.length) {
      const component = comps[i];
      if (component.kind === 'Portal Public Link' && component.inline && isVisible(component)) {
        const group: PortalPublicLink[] = [component as PortalPublicLink];
        while (
          i + 1 < comps.length &&
          comps[i + 1].kind === 'Portal Public Link' &&
          comps[i + 1].inline &&
          isVisible(comps[i + 1])
        ) {
          i++;
          group.push(comps[i] as PortalPublicLink);
        }
        const alignment = group[0].alignment || 'left';
        const justifyClass = alignment === 'center' ? 'justify-center items-center' : alignment === 'right' ? 'justify-end items-end' : 'justify-start items-start';
        result.push(
          <div key={`inline-group-${i}`} className={`flex flex-col sm:flex-row gap-4 mb-4 ${justifyClass}`}>
            {group.map((link, gi) => (
              <PortalPublicLinkComponent key={gi} link={link} info={{}} isInGroup />
            ))}
          </div>
        );
      } else {
        result.push(renderComponent(component, i));
      }
      i++;
    }
    return result;
  }, [renderComponent, isLoggedIn]);


  const notEnabledMain = (
    <div className="w-full h-full flex flex-col justify-center">
      <p className="text-center text-xl">{L.applicant.applicant_portal_not_enabled}</p>
    </div>
  );

  const isMobile = useIsMobile()

  // The page content rendered beneath the portal header in the shell.
  const portalContent = (
    <div>
      {isComponentsLoading || isCandidatesLoading ? (
        <div className="flex justify-center items-center h-screen">
          <SpacedSpinner className="h-20 w-20 text-gray-400" /> {/* TODO: replace with Page Loading Component once we have one */}
        </div>
      ) : (
        <div className="max-w-4xl mx-auto p-2 sm:p-6 lg:p-8">
          {renderComponentList(components)}
        </div>
      )}
    </div>
  );

  const main = (
    <div className="max-w-4xl mx-auto applicant-led p-2 pb-36">
      {isComponentsLoading || isCandidatesLoading ? (
        <div className="flex justify-center items-center h-screen">
          <SpacedSpinner className="h-20 w-20 text-gray-400" /> {/* TODO: replace with Page Loading Component once we have one */}
        </div>
      ) : (
        renderComponentList(components)
      )}
    </div>
  );

  const useNewPortalUI = !!publicConfig.applicantPortal && (props.Viewer === 'applicant' || !!props.isDistroPreview);

  // Embedded: hand back bare content, not three column page
  if (props.embedded) {
    return <>{applicantPortalPageNotEnabled ? notEnabledMain : main}</>;
  }

  if (applicantPortalPageNotEnabled) {
    return <ThreeColumnPage main={notEnabledMain} Viewer={props.Viewer} nav={nav} applicantPortal isDistroPreview={props.isDistroPreview}/>;
  }
  if (useNewPortalUI || isMobile) {
    return <ApplicantPortalShell main={portalContent} nav={nav} Viewer={props.Viewer} isLoggedIn={isLoggedIn} isDistroPreview={props.isDistroPreview} />;
  }
  return <ThreeColumnPage main={main} Viewer={props.Viewer} nav={nav} applicantPortal isDistroPreview={props.isDistroPreview}/>;
}
